import "./update.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Select from "react-select";
import Navbar from "../../components/navbar/Navbar";
import DriveFolderUploadOutlinedIcon from "@mui/icons-material/DriveFolderUploadOutlined";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { absentformInputs } from "../../formSource";
import { serverUrl } from "../../temp";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const statusOptions = [
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

const isImage = (file) => {
  if (!file) return false;

  // Handle File objects
  if (file instanceof File) {
    return file.type.startsWith("image/");
  }

  // Handle URL strings
  if (typeof file === "string") {
    return [".jpg", ".jpeg", ".png"].some((ext) =>
      file.toLowerCase().endsWith(ext)
    );
  }

  return false;
};

const isPDF = (file) => {
  if (!file) return false;

  // Handle File objects
  if (file instanceof File) {
    return file.type === "application/pdf";
  }

  // Handle URL strings
  if (typeof file === "string") {
    return file.toLowerCase().endsWith(".pdf");
  }

  return false;
};

const AbsentFormUpdate = ({ title }) => {
  const location = useLocation();
  const absentformId = location.pathname.match(
    /\/update-absent-form\/(\d+)/
  )?.[1];
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [inputValues, setInputValues] = useState({});
  const [token] = useState(localStorage.getItem("token"));
  const navigate = useNavigate();

  const redirectToLogin = () => {
    toast.warning("Please login first to access this page", {
      autoClose: 2000,
      onClose: () => (window.location.href = "/"),
    });
  };

  useEffect(() => {
    const fetchLeaveForm = async () => {
      try {
        const response = await fetch(
          `${serverUrl}/student/Aform/${absentformId}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch absent form");
        }
        const data = await response.json();

        if (data.code !== "200") {
          toast.error(data.message || "Failed to load absent form");
          return;
        }

        setInputValues(data.data);
        setFilePreview(data.data.absent_file_url);

        setSelectedStatus(
          data.data.status == 1
            ? { value: 1, label: "Active" }
            : { value: 0, label: "Inactive" }
        );
      } catch (error) {
        toast.error("Error fetching absent form data");
        console.error(error);
      }
    };

    if (absentformId) {
      fetchLeaveForm();
    }
  }, [absentformId]);

  const handleInputChange = (e) => {
    setInputValues({
      ...inputValues,
      [e.target.name]: e.target.value,
    });
  };

  const handleStatusSelectChange = (selectedOption) => {
    setSelectedStatus(selectedOption);
    setInputValues({
      ...inputValues,
      status: selectedOption.value,
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    if (!isImage(selectedFile) && !isPDF(selectedFile)) {
      toast.error("Please upload an image (JPEG, PNG) or PDF file");
      return;
    }

    setFile(selectedFile);

    // Set preview
    if (isImage(selectedFile)) {
      setFilePreview(URL.createObjectURL(selectedFile));
    } else if (isPDF(selectedFile)) {
      setFilePreview("/images/pdf_icon.png");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const toastId = toast.loading("Updating absent form...");
    const adminData = JSON.parse(localStorage.getItem("admindata"));
    const formData = new FormData();
    formData.append("center_id", localStorage.getItem("center_id"));
    formData.append("student_id", localStorage.getItem("student_id"));
    formData.append("user_id", adminData.id);
    formData.append("reason", inputValues.reason);
    formData.append("date", inputValues.date);
    if (file) {
      formData.append("logo", file);
    }
    formData.append("status", inputValues.status || 1);

    try {
      const response = await fetch(
        `${serverUrl}/student/upd_Aform/${absentformId}`,
        {
          method: "PUT",
          body: formData,
        }
      );

      const data = await response.json();

      if (data.code >= 400) {
        toast.update(toastId, {
          render: data.message || "Failed to update absent form",
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
        return;
      }

      toast.update(toastId, {
        render: data.message || "Absent form updated successfully!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });

      setTimeout(() => {
        navigate("/student-attendance/absent-form");
      }, 2000);
    } catch (error) {
      toast.update(toastId, {
        render: "An error occurred while updating the form",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      console.error(error);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      {!token && redirectToLogin()}
      {token && (
        <div className="update">
          <Sidebar />
          <div className="updateContainer">
            <Navbar />
            <div className="top">
              <h1>{title}</h1>
            </div>
            <div className="bottom">
              <div className="left">
                <div className="filePreview">
                  {filePreview ? (
                    isImage(file || inputValues.absent_file_url) ? (
                      <img
                        src={filePreview}
                        alt="Absent Form Preview"
                        className="itemImg"
                      />
                    ) : (
                      <img
                        src="/images/pdf_icon.png"
                        alt="Absent Form PDF"
                        className="itemImg"
                      />
                    )
                  ) : (
                    <img
                      src="/images/upload_file.png"
                      alt="Default File"
                      className="itemImg"
                    />
                  )}
                  {file ? (
                    <p className="fileName">{file.name}</p>
                  ) : inputValues.absent_file_url ? (
                    <p className="fileName">
                      {inputValues.absent_file_url.split("/").pop()}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="right">
                <form onSubmit={handleUpdate}>
                  <div className="formInput">
                    <label htmlFor="file">
                      Absent Form (Image/PDF):{" "}
                      <DriveFolderUploadOutlinedIcon className="icon" />
                    </label>
                    <input
                      type="file"
                      id="file"
                      accept="image/*,.pdf"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                  </div>
                  {absentformInputs.map((input) => (
                    <div className="formInput" key={input.id}>
                      <label>{input.label}</label>
                      {input.fieldName === "status" ? (
                        <Select
                          options={statusOptions}
                          name={input.fieldName}
                          value={selectedStatus}
                          onChange={handleStatusSelectChange}
                          required
                        />
                      ) : (
                        <input
                          type={input.type}
                          placeholder={input.placeholder}
                          name={input.fieldName}
                          value={inputValues[input.fieldName] || ""}
                          onChange={handleInputChange}
                          required
                        />
                      )}
                    </div>
                  ))}
                  <div style={{ clear: "both" }} className="formUpdate">
                    <button type="submit" style={{ float: "right" }}>
                      Update
                    </button>
                    <button
                      type="button"
                      style={{ float: "right", marginRight: "10px" }}
                      onClick={() =>
                        navigate("/student-attendance/absent-form")
                      }
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AbsentFormUpdate;
